import { Popover, PopoverContent, PopoverTrigger } from '@ibsys/kit';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  EMPTY_RESULT,
  getEffectiveEvaluationResult,
  getEvaluationMaxScore,
  getExpertReportResultText,
  getSystemEvaluationResult,
  hasPointsScale,
  useUpdateExpertReportBlockManualRating,
} from '@/entities/expert-report';
import { cn } from '@/shared/lib';
import { TEXT_DIFF_ADDED_CLASS, TEXT_DIFF_REMOVED_CLASS, Tooltip } from '@/shared/ui';
import { EvaluationBlockRatingEditor } from './EvaluationBlockRatingEditor';
import { EvaluationBlockValueEditor } from './EvaluationBlockValueEditor';
import type { FlatRow } from './ExpertReportTab';

type Props = {
  row: FlatRow;
  conversationId: number;
  expertReportId: number;
};

/**
 * Правка результата блока по месту: окно открывается на самом результате и предлагает те формы,
 * которые несёт шкала блока, — ввод балла, выбор значения справочника или, у совмещённой шкалы,
 * и то, и другое. Выставленное заменяет результат целиком, а совпавшее с полученным системой
 * снимает ручной результат вовсе.
 *
 * Строка читается действующим результатом: правленый показан сравнением — результат системы
 * перечёркнут, действующий подсвечен, в какой бы форме каждый из них ни был. Автор и время правки
 * в заключении не показываются — они видны в разделе «Обратная связь».
 */
export const EvaluationBlockResultEditor: FC<Props> = ({ row, conversationId, expertReportId }) => {
  const { t } = useTranslation(['dialogs']);
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState(String(row.manualRating ?? row.rating ?? ''));
  const isCancelledRef = useRef(false);
  const { mutateAsync, isPending } = useUpdateExpertReportBlockManualRating();

  const result = getEffectiveEvaluationResult(row);
  const maxRating = getEvaluationMaxScore(row);
  const canRate = hasPointsScale(row);
  const possibleValues = row.possibleValues ?? [];
  const isManual = row.manualRating !== null || row.manualValue !== null;
  // Размерность правку не сопровождает: перечёркнутым стоит сам результат системы, а единица,
  // в которой он читается, — уже за правкой, у действующего балла
  const systemText = getExpertReportResultText({ ...getSystemEvaluationResult(row), unit: null });

  // Пока окно закрыто, поле следует за пришедшим с сервера результатом
  useEffect(() => {
    if (!isOpen) setInputValue(String(row.manualRating ?? row.rating ?? ''));
  }, [isOpen, row.manualRating, row.rating]);

  const parsed = inputValue.trim() === '' ? null : Number(inputValue);
  const isOutOfRange =
    parsed !== null && !Number.isNaN(parsed) && (parsed < 0 || (maxRating !== null && parsed > maxRating));

  const saveRating = useCallback(async () => {
    const trimmed = inputValue.trim();
    // Пустое поле ничего не меняет: сохранять нечего
    if (trimmed === '') return;
    const value = Number(trimmed);
    if (Number.isNaN(value) || value < 0 || (maxRating !== null && value > maxRating)) return;
    // Балл, совпавший с рассчитанным системой, ручным не считается
    const nextManual = value === row.rating ? null : value;
    // Запроса не нужно, только когда блок и так читается ровно так: у блока, читающегося
    // выставленным значением, тот же балл снимает это значение и меняет результат
    if (nextManual === row.manualRating && row.manualValue === null) return;
    try {
      await mutateAsync({ conversationId, expertReportId, blockId: row.blockId, manualRating: nextManual });
    } catch {
      // Ошибку показал глобальный перехватчик запросов; поле возвращаем к сохранённому результату
      setInputValue(String(row.manualRating ?? row.rating ?? ''));
    }
  }, [conversationId, expertReportId, row, maxRating, inputValue, mutateAsync]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      // Каждое открытие начинается с чистой пометки: иначе отмена прошлой правки погасила бы
      // сохранение следующей
      if (open) isCancelledRef.current = false;
      if (!open) {
        if (isCancelledRef.current) {
          isCancelledRef.current = false;
          setInputValue(String(row.manualRating ?? row.rating ?? ''));
        } else if (canRate) {
          void saveRating();
        }
      }
      setIsOpen(open);
    },
    [saveRating, canRate, row.manualRating, row.rating],
  );

  const handleSelectValue = useCallback(
    async (selected: string) => {
      // Окно закрывается самим выбором, и набранный балл в этом закрытии не сохраняется:
      // результат у блока один, и выставляет его выбранное значение
      isCancelledRef.current = true;
      setIsOpen(false);
      // Значение, совпавшее с полученным системой, ручным не считается
      const nextManual = selected === row.value ? null : selected;
      if (nextManual === row.manualValue && row.manualRating === null) return;
      try {
        await mutateAsync({ conversationId, expertReportId, blockId: row.blockId, manualValue: nextManual });
      } catch {
        // Ошибку показал глобальный перехватчик запросов; действующий результат придёт с сервера
      }
    },
    [conversationId, expertReportId, row, mutateAsync],
  );

  const handleCancel = useCallback(() => {
    isCancelledRef.current = true;
    // Контролируемый Popover не зовёт onOpenChange при закрытии снаружи — закрываем общим путём:
    // иначе набранное останется в поле и всплывёт при следующем открытии
    handleOpenChange(false);
  }, [handleOpenChange]);

  /**
   * Escape закрывает окно отменой.
   *
   * Клавишу перехватывает сам Popover, и делает это раньше поля: без этой пометки закрытие пошло бы
   * обычным путём — с сохранением, то есть Escape сохранял бы балл вместо того, чтобы отменить правку.
   */
  const handleEscapeKeyDown = useCallback(() => {
    isCancelledRef.current = true;
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        // Балл вне границ блока не сохраняется — окно остаётся открытым с отказом
        if (isOutOfRange) return;
        // Контролируемый Popover не зовёт onOpenChange при закрытии снаружи — сохраняем явно
        handleOpenChange(false);
      }
      // Escape здесь не обрабатываем: клавишу перехватывает сам Popover, он же закрывает окно —
      // отмену помечает `handleEscapeKeyDown`
    },
    [isOutOfRange, handleOpenChange],
  );

  const getEditLabel = () => {
    if (canRate && possibleValues.length) return t('dialogs:expertReport.editResult');
    return canRate ? t('dialogs:expertReport.editRating') : t('dialogs:expertReport.editValue');
  };
  const editLabel = getEditLabel();

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <span
          role="button"
          tabIndex={-1}
          aria-label={editLabel}
          className={cn(
            'shrink-0 whitespace-nowrap cursor-pointer hover:text-primary/80',
            isManual ? 'text-foreground' : 'text-muted-foreground',
          )}
        >
          {/* Подчёркивание — на самих значениях: заданное на всей строке, оно прошло бы и по
              результату системы, а снятое у него — погасило бы вместе с собой и перечёркивание.
              Название действия стоит на самих значениях, а не на строке целиком: у результата,
              выставленного вручную, своя подсказка, и подсказка строки спорила бы с ней */}
          {isManual ? (
            <>
              <Tooltip tooltip={editLabel}>
                <s className={cn(TEXT_DIFF_REMOVED_CLASS, 'ml-0 mr-1')}>{systemText ?? EMPTY_RESULT}</s>
              </Tooltip>
              {/* Подсказка держится на самом результате, а фокус закрывшееся окно возвращает кнопке
                  строки — сама собой подсказка не всплывает, гасить её не от чего */}
              <Tooltip
                tooltip={
                  result.score != null
                    ? t('dialogs:expertReport.manualRatingHint')
                    : t('dialogs:expertReport.manualValueHint')
                }
              >
                <mark className={cn(TEXT_DIFF_ADDED_CLASS, 'mx-0 underline')}>{result.score ?? result.value}</mark>
              </Tooltip>
            </>
          ) : (
            <Tooltip tooltip={editLabel}>
              <span className="underline">{result.score ?? result.value ?? EMPTY_RESULT}</span>
            </Tooltip>
          )}
          {/* Максимум и размерность стоят только у результата-балла: результат-значение ни того,
              ни другого не несёт. Размерность — за пределами подчёркивания: правится балл,
              а не единица, в которой он читается */}
          {result.score != null && result.maxScore != null && (
            <>
              {' '}
              <Tooltip tooltip={editLabel}>
                <span className="underline">{`/ ${result.maxScore}`}</span>
              </Tooltip>
            </>
          )}
          {result.unit ? ` ${result.unit}` : ''}
        </span>
      </PopoverTrigger>
      <PopoverContent
        className={cn(canRate ? 'w-64 p-3' : 'w-auto p-1')}
        align="start"
        onEscapeKeyDown={handleEscapeKeyDown}
      >
        <div className="flex flex-col gap-1">
          {canRate && (
            <EvaluationBlockRatingEditor
              value={inputValue}
              onChange={setInputValue}
              onKeyDown={handleKeyDown}
              maxRating={maxRating}
              unit={row.unit}
              isOutOfRange={isOutOfRange}
              isPending={isPending}
              onClose={handleCancel}
            />
          )}
          {possibleValues.length > 0 && (
            <EvaluationBlockValueEditor
              possibleValues={possibleValues}
              checkedValue={result.value ?? null}
              isPending={isPending}
              onSelect={handleSelectValue}
            />
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};
